import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import { useAnalytics } from '@cosuno/analytics-frontend';
import { doesErrorMatchCode, removeTypename, trackedQuery } from '@cosuno/apollo';
import { isCompanyFeatureEnabled, useFeatureFlagsSetter } from '@cosuno/feature-flags-frontend';
import { detectLanguage, setCurrentLanguage } from '@cosuno/localization';
import { useEffectOnce, useQueryParamBoolean } from '@cosuno/react-hooks';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { COMPANY_TYPE, USER_STATUS } from '#@/__gql__/graphql';

import {
  AUTH_STATUS,
  AUTH_TOKEN_EXPIRES_AFTER_DURATION,
  AUTHENTICATION_ERROR_TYPES,
  INCREASED_SECURITY_FEATURES_AUTH_TOKEN_EXPIRES_AFTER_DURATION,
} from '../../constants';
import type { AuthContextBase } from '../../contexts/authContext';
import type { AuthLocationState } from '../../types';
import { mapUserToUserData } from '../../utils';
import { useInactivityTracker } from '../useInactivityTracker';
import useLocalStorageState from '../useLocalStorageState';
import {
  queryCheckWorkspace,
  queryCurrentAdmin,
  queryCurrentUser,
  querySSORedirectUrl,
} from './api';
import type { AuthState, CurrentUser_me, LoginParams, LoginWithSSOParams } from './types';
import { useUtmParametersTracking } from './useUtmParametersTracking';
import { mapToCompanyFeatureFlags, mapToUserFeatureFlags } from './utils';

const TRACK_AUTH_SIGN_IN_PARAM = 'trackAuthSignIn';

class AuthenticationError extends Error {
  constructor() {
    super('Authentication Error');
  }
}

const useAuthState = () => {
  const [authStatus, setAuthStatus] = useState<AUTH_STATUS>(AUTH_STATUS.authenticating);
  const { data: currentUser } = useQuery(queryCurrentUser, { fetchPolicy: 'cache-only' });

  const authState = useMemo<AuthState>(() => {
    if (authStatus !== AUTH_STATUS.authenticated) {
      return { status: authStatus };
    }

    if (!currentUser) {
      return { status: AUTH_STATUS.anonymous };
    }

    return {
      status: authStatus,
      companyType: COMPANY_TYPE[currentUser.me.company.type],
      user: removeTypename(currentUser.me),
    };
  }, [authStatus, currentUser]);

  return { authState, setAuthStatus };
};

export const useAuthentication = (props: {
  apiUserEndpoint: string;
  ipInfoToken: string | null;
  reCaptchaKey: string | null;
  isTestRun: boolean;
  routes: AuthContextBase['routes'];
}): AuthContextBase => {
  const { apiUserEndpoint, ipInfoToken, reCaptchaKey, isTestRun, routes } = props;
  const { i18n } = useTranslation();
  const { authState, setAuthStatus } = useAuthState();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const apolloClient = useApolloClient();
  const history = useHistory();
  const [shouldTrackAuthSignIn, setShouldTrackAuthSignIn] = useState(false);
  useQueryParamBoolean(TRACK_AUTH_SIGN_IN_PARAM, setShouldTrackAuthSignIn);
  const [userIdentityTracked, setUserIdentityTracked] = useState(false);
  const lastLogoutTime = useRef<number | null>(null);
  const { setUtmParameterCookie, deleteUtmParametersCookie } = useUtmParametersTracking();
  const { trackEvent, trackPageView, resetUserIdentity, trackUserIdentity } = useAnalytics();
  const { setUserFeatureFlags, setCompanyFeatureFlags } = useFeatureFlagsSetter();

  const [storedUserId, setStoredUserId] = useLocalStorageState<string | null>(
    'useAuthentication.userId',
    null,
  );

  const [currentUserQuery, { data: currentUserData }] = useLazyQuery(queryCurrentUser);

  useEffect(() => {
    if (!currentUserData) {
      setCompanyFeatureFlags(null);
      setUserFeatureFlags(null);
    } else {
      setUserFeatureFlags(mapToUserFeatureFlags(currentUserData.me));
      setCompanyFeatureFlags(mapToCompanyFeatureFlags(currentUserData.me));
    }
  }, [currentUserData, setCompanyFeatureFlags, setUserFeatureFlags]);

  if (shouldTrackAuthSignIn && userIdentityTracked) {
    trackEvent('authSignIn');
    setShouldTrackAuthSignIn(false);
  }

  const updateUser = useCallback(
    async (user: CurrentUser_me | null) => {
      if (!user) {
        setAuthStatus(AUTH_STATUS.anonymous);
        setStoredUserId(null);
        await setCurrentLanguage(detectLanguage(), i18n);
        return;
      }

      setAuthStatus(AUTH_STATUS.authenticated);
      setStoredUserId(user.id);
      await setCurrentLanguage(user.language, i18n);
    },
    [i18n, setAuthStatus, setStoredUserId],
  );

  /** Gets the auth token cookie */
  const authenticate = useCallback(
    async (credentials?: { loginToken: string; mfaToken?: string } | { userId: string }) => {
      const response = await axios.post(apiUserEndpoint, credentials, {
        withCredentials: true,
        validateStatus: (status) => [200, 401].includes(status),
      });

      if (response.status !== 200) {
        await updateUser(null);
        throw new AuthenticationError();
      }
    },
    [apiUserEndpoint, updateUser],
  );

  const login = useCallback(
    async ({
      loginToken,
      mfaToken,
      redirectTo = routes.defaultRedirectToAfterLogin,
      trackAuthSignIn,
    }: LoginParams) => {
      await authenticate({ loginToken, mfaToken });

      // prepare the redirect url
      const url = new URL(redirectTo, window.location.origin);
      if (trackAuthSignIn) {
        // provide ability to detect that tracking "authSignIn" event required after resetting js context
        url.searchParams.set(TRACK_AUTH_SIGN_IN_PARAM, 'true');
      }

      // reset js context to get rid of any active tracking tools
      // https://github.com/cosuno/cosuno/pull/2094#discussion_r622360335
      // Setting href instead of pathname allows us to pass query params.
      window.location.href = url.toString();
    },
    [authenticate, routes],
  );

  const logout = useCallback(
    async (options?: {
      openRoute?: string;
      query?: string;
      redirectBack?: boolean;
      loginState?: AuthLocationState;
    }) => {
      const pathnameBeforeLogout = window.location.pathname;

      // Invalidates cookie in DB and deletes it on the client
      try {
        await axios.delete(apiUserEndpoint, {
          withCredentials: true,
        });
        // eslint-disable-next-line no-empty
      } catch {}

      resetUserIdentity();
      await updateUser(null);
      void apolloClient.clearStore();
      lastLogoutTime.current = Date.now();

      const openRoute = options?.openRoute || routes.login;
      history.push({
        pathname: openRoute,
        search: options?.query,
        state: {
          ...options?.loginState,
          redirect: options?.redirectBack ? pathnameBeforeLogout : undefined,
        },
      });
    },
    [resetUserIdentity, updateUser, apolloClient, routes, history, apiUserEndpoint],
  );

  const inactivityTracker = useInactivityTracker({
    onInactive: () =>
      void logout({
        query: '?showInactivityMessage=true',
        redirectBack: true,
      }),
  });

  const isInactivityTrackerEnabled =
    authState.status === AUTH_STATUS.authenticated &&
    isCompanyFeatureEnabled(authState.user.company, 'feature-increased-security-features');

  useEffect(() => {
    // If the user is logged out, we don't know whether increased security features are enabled,
    // so we always stop the inactivity tracker.
    if (authState.status !== AUTH_STATUS.authenticated) {
      inactivityTracker.stop();
      return;
    }

    if (isInactivityTrackerEnabled) {
      inactivityTracker.start();
    }

    return inactivityTracker.stop;
    // Careful: Ensure these dependencies are stable, otherwise they will make the inactivity
    // tracker unreliable and cause flaky tests.
  }, [authState.status, isInactivityTrackerEnabled, inactivityTracker]);

  const fetchCurrentUser = useCallback(
    async (options: { trackUser?: boolean } = { trackUser: true }) => {
      const operationStartTime = Date.now();
      const response = await currentUserQuery();

      if (lastLogoutTime.current && operationStartTime <= lastLogoutTime.current) return;

      if (response.error) {
        if (
          doesErrorMatchCode(response.error, AUTHENTICATION_ERROR_TYPES.unauthenticated) &&
          authState.status === AUTH_STATUS.authenticated
        ) {
          // If we are unauthenticated, we reset the user identitiy and unset the current user.
          // We do not trigger logout because it may be that we are in a context where no
          // authentication is needed.
          resetUserIdentity();
          await updateUser(null);
        }

        return;
      }

      if (!response.data) {
        return;
      }

      await updateUser(response.data.me);

      if (options.trackUser) {
        trackUserIdentity(mapUserToUserData(response.data.me));
        setUserIdentityTracked(true);
      }

      deleteUtmParametersCookie();
    },
    [
      currentUserQuery,
      updateUser,
      deleteUtmParametersCookie,
      authState.status,
      resetUserIdentity,
      trackUserIdentity,
    ],
  );

  const fetchCurrentAdmin = useCallback(async (): Promise<boolean> => {
    const { data } = await trackedQuery(apolloClient, {
      query: queryCurrentAdmin,
    });

    if (data.adminMe) {
      setIsSuperAdmin(true);
      return true;
    }

    return false;
  }, [apolloClient]);

  // Set UTM parameter cookie once on mount
  useEffectOnce(() => {
    setUtmParameterCookie();
  });

  // Re-authenticate on mount, or when `storedUserId` changes (authentication in different tab)
  useEffect(() => {
    if (authState.status === AUTH_STATUS.anonymous && !storedUserId) {
      // Stored state consistent with local state: No need to authenticate
      return;
    }

    if (authState.status === AUTH_STATUS.authenticated && authState.user.id === storedUserId) {
      // Stored state consistent with local state: No need to authenticate
      return;
    }

    void authenticate()
      .catch(() => {})
      .then(() => fetchCurrentAdmin())
      .then((isSuperAdminResult) =>
        fetchCurrentUser({ trackUser: !isSuperAdminResult })
          .catch(() => {})
          // We want to call trackPageview after trackUserIdentity, so Segment doesn't interpret the
          // session as anonymous.
          .finally(() => {
            if (!isSuperAdminResult) trackPageView();
          }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedUserId]);

  // Periodically re-authenticate (generates new auth token and updates user)
  useEffect(() => {
    if (authState.status !== AUTH_STATUS.authenticated) return undefined;

    const tokenDurationMilliseconds = isCompanyFeatureEnabled(
      authState.user.company,
      'feature-increased-security-features',
    )
      ? INCREASED_SECURITY_FEATURES_AUTH_TOKEN_EXPIRES_AFTER_DURATION.asMilliseconds()
      : AUTH_TOKEN_EXPIRES_AFTER_DURATION.asMilliseconds();
    const refreshIntervalMilliseconds = tokenDurationMilliseconds / 2;

    const intervalId = setInterval(
      () =>
        void authenticate()
          .then(() => fetchCurrentUser())
          .catch(() => {}),
      refreshIntervalMilliseconds,
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [authState, authenticate, fetchCurrentUser]);

  const loginWithSSO = useCallback(
    async (params: LoginWithSSOParams) => {
      const { data } = await trackedQuery(apolloClient, {
        query: querySSORedirectUrl,
        variables: params,
      });

      window.location.href = data.ssoRedirectUrl;
    },
    [apolloClient],
  );

  const switchToWorkspace = useCallback(
    async (workspaceId: string, redirectTo?: string) => {
      if (authState.status !== AUTH_STATUS.authenticated) {
        return;
      }

      const ssoRequired = Boolean(
        authState.user.workspaces.find(({ id }) => id === workspaceId)?.ssoEnabled,
      );

      if (ssoRequired) {
        return loginWithSSO({
          email: authState.user.email,
          expectedUserStatus: USER_STATUS.active,
          redirectPath: redirectTo,
          workspaceId,
        });
      }

      const {
        data: {
          checkWorkspace: { loginToken },
        },
      } = await trackedQuery(apolloClient, {
        query: queryCheckWorkspace,
        variables: { workspaceId },
      });

      await login({ loginToken, redirectTo });
    },
    [login, apolloClient, authState, loginWithSSO],
  );

  return useMemo(
    () => ({
      authState,
      isSuperAdmin,
      ipInfoToken,
      reCaptchaKey,
      logoutOnUnauthenticated: !isTestRun,
      isTrackingEnabled: !isTestRun,
      routes,
      login,
      authenticate,
      logout,
      fetchCurrentUser,
      loginWithSSO,
      switchToWorkspace,
      deleteUtmParametersCookie,
    }),
    [
      authState,
      isSuperAdmin,
      ipInfoToken,
      reCaptchaKey,
      isTestRun,
      routes,
      login,
      authenticate,
      logout,
      fetchCurrentUser,
      loginWithSSO,
      switchToWorkspace,
      deleteUtmParametersCookie,
    ],
  );
};
